import { useCallback } from "react";
import { useCreate, useNotify } from "react-admin";
import { CreateInDialog } from "@/components/common/CreateInDialog";
import {
  ResourceName,
  type ContractLaserRequest,
  type PolicyCreate as PolicyCreateType,
  type EndorsementCreateConfig,
  type LaseredClaimantCreateEntry,
  type ValidAny,
} from "@/types";
import { httpClient, getPartnerAwareApiUrl, stripPolicyTerminationDate } from "@/providers/dataProvider/dataProvider";
import { laseredConditionApi } from "../hooks/useLaseredConditions";

/** The entry's claimant as the user picked it, for failure toasts. */
const entryName = (entry: LaseredClaimantCreateEntry): string => entry.claimant_display_name || "a claimant";

/**
 * Write an entry's conditional lasers (Feature #3502) after its laser exists.
 * Returns the names of those that failed; failures do not undo the entry.
 */
const createEntryConditions = async (policyId: string, entry: LaseredClaimantCreateEntry): Promise<string[]> => {
  const failed: string[] = [];
  for (const c of entry.lasered_conditions ?? []) {
    try {
      await laseredConditionApi.create(policyId, entry.claimant_id_ref, c);
    } catch {
      failed.push(`${c.lasered_condition_name} for ${entryName(entry)}`);
    }
  }
  return failed;
};

/**
 * Create each queued lasered claimant on the new policy, then that entry's
 * conditional lasers (Feature #3502), in order. A failed laser skips its
 * conditions; a failed condition does not undo its laser. A Conditional entry
 * writes its standing deductible first when it has one; without one its
 * conditional lasers are the laser, so the entry fails when none of them saves.
 * Failures come back by name, so the toast can say which (as ClaimantList
 * does). Exported for tests.
 */
export const createLaseredClaimantEntries = async (
  policyId: string,
  entries: LaseredClaimantCreateEntry[],
  createLaser: (resource: string, data: Record<string, unknown>) => Promise<unknown>
): Promise<{
  created: number;
  failed: number;
  conditionFailures: number;
  /** Claimants whose laser was not created. */
  failedClaimants: string[];
  /** "ESRD for Dustin Henderson" per conditional laser not created. */
  failedConditions: string[];
}> => {
  const laseredResource = `policies/${policyId}/lasered-claimants`;
  let created = 0;
  const failedClaimants: string[] = [];
  const failedConditions: string[] = [];

  for (const entry of entries) {
    try {
      if (entry.entry_kind === "contract" || entry.entry_kind === "exclusion") {
        // Contract laser / full exclusion: these live below the standard
        // CRUD surface, so PUT directly via the shared httpClient — mirrors
        // useContractLaser.ts. The two flags are mutually exclusive.
        const body: ContractLaserRequest =
          entry.entry_kind === "exclusion"
            ? { is_contract_lasered: false, is_contract_excluded: true }
            : {
                is_contract_lasered: true,
                is_contract_excluded: false,
                amt_contract_lasered_deductible: entry.amt_contract_lasered_deductible ?? 0,
                contract_incurred_start_date: entry.contract_incurred_start_date,
                contract_incurred_end_date: entry.contract_incurred_end_date,
                contract_paid_start_date: entry.contract_paid_start_date,
                contract_paid_end_date: entry.contract_paid_end_date,
                excluded_procedures: entry.excluded_procedures,
              };
        await httpClient(
          `${getPartnerAwareApiUrl()}/policies/${policyId}/lasered-claimants/${entry.claimant_id_ref}/contract-laser`,
          { method: "PUT", body: JSON.stringify(body) }
        );
        created++;
        failedConditions.push(...(await createEntryConditions(policyId, entry)));
        continue;
      }

      if (entry.entry_kind === "conditional" && !(entry.amt_lasered_deductible > 0)) {
        // No standing deductible: the conditional lasers alone.
        const failed = await createEntryConditions(policyId, entry);
        if (failed.length > 0 && failed.length === (entry.lasered_conditions ?? []).length) {
          failedClaimants.push(entryName(entry));
        } else {
          created++;
          failedConditions.push(...failed);
        }
        continue;
      }

      const laseredPayload: Record<string, unknown> = {
        claimant_id_ref: entry.claimant_id_ref,
        laser_type: entry.laser_type,
        amt_lasered_deductible: entry.amt_lasered_deductible,
        amt_incremental_lasered_deductible: entry.amt_incremental_lasered_deductible,
      };
      if (entry.effective_date) laseredPayload.effective_date = entry.effective_date;

      await createLaser(laseredResource, laseredPayload);
      created++;
      failedConditions.push(...(await createEntryConditions(policyId, entry)));
    } catch {
      failedClaimants.push(entryName(entry));
    }
  }
  return {
    created,
    failed: failedClaimants.length,
    conditionFailures: failedConditions.length,
    failedClaimants,
    failedConditions,
  };
};

/**
 * Toasts after the lasered claimants are written: what was assigned, and which
 * claimants and conditional lasers failed, by name. Exported for tests.
 */
export const laseredEntryToasts = ({
  created,
  failed,
  conditionFailures,
  failedClaimants,
  failedConditions,
}: Awaited<ReturnType<typeof createLaseredClaimantEntries>>): { message: string; type: "success" | "warning" }[] => {
  const out: { message: string; type: "success" | "warning" }[] = [];
  if (created > 0 && failed === 0) {
    out.push({ message: `${created} lasered claimant${created > 1 ? "s" : ""} assigned`, type: "success" });
  } else if (failed > 0) {
    out.push({
      message: `${created} lasered claimant${created !== 1 ? "s" : ""} assigned, ${failed} failed (${failedClaimants.join(", ")}) — you can add ${failed !== 1 ? "them" : "it"} from the policy page`,
      type: "warning",
    });
  }
  if (conditionFailures > 0) {
    out.push({
      message: `${conditionFailures === 1 ? "A conditional laser" : `${conditionFailures} conditional lasers`} could not be saved (${failedConditions.join(", ")}) — add ${conditionFailures !== 1 ? "them" : "it"} from the policy's Lasered Claimants tab`,
      type: "warning",
    });
  }
  return out;
};

interface PolicyCreateDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * When provided, pre-populates the client field on the manual entry form.
   * The field remains editable so the user can change the client if needed.
   */
  defaultClientId?: string;
}

/**
 * `PolicyCreateDialog` is the manual policy-entry dialog. It wraps
 * `CreateInDialog` with policy-specific defaults, payload transformation, and
 * the post-create steps that fan out endorsements and lasered claimants to
 * their own endpoints.
 *
 * It is shared between the `PolicyCreate` page ("Create Manually" CTA) and the
 * client screen's "No Active Policy" banner, where it opens directly with the
 * originating client pre-selected (story #4972) — bypassing the intermediate
 * create page now that file-upload create is hidden (#4649).
 */
export const PolicyCreateDialog: React.FC<PolicyCreateDialogProps> = ({ open, onClose, defaultClientId }) => {
  const [createRecord] = useCreate();
  const notify = useNotify();

  /** Strip client-only fields before sending to the backend */
  const transformPolicyPayload = useCallback((data: ValidAny) => {
    const {
      endorsement_configs: _endorsements,
      lasered_claimants: _lasered,
      ...policyPayload
    } = data as PolicyCreateType;
    // #7320: TerminationDateInput unmounts when status leaves "cancelled" but the
    // form keeps the value (shouldUnregister is false), and the API rejects a
    // termination_date on any other status. Same rule the update path applies.
    return stripPolicyTerminationDate(policyPayload as Record<string, unknown>);
  }, []);

  /** After policy creation, create endorsements and lasered claimants via their respective endpoints */
  const handlePostCreate = useCallback(
    async (newPolicy: ValidAny, formData: ValidAny) => {
      const policyId = newPolicy.id as string;
      const { endorsement_configs, lasered_claimants } = formData as PolicyCreateType;

      // ── Create endorsements ──
      if (endorsement_configs) {
        const endorsementResource = `policies/${policyId}/endorsements`;
        const endorsementEntries = Object.values(endorsement_configs).filter(
          (cfg): cfg is EndorsementCreateConfig => cfg !== null && cfg !== undefined && cfg.is_enabled
        );

        if (endorsementEntries.length > 0) {
          let created = 0;
          let failed = 0;

          for (const cfg of endorsementEntries) {
            try {
              await createRecord(
                endorsementResource,
                {
                  data: {
                    type: cfg.type,
                    is_enabled: cfg.is_enabled,
                    valid_period_start: cfg.valid_period_start ?? "",
                    valid_period_end: cfg.valid_period_end ?? "",
                    config: cfg.config ?? {},
                  },
                },
                { returnPromise: true }
              );
              created++;
            } catch {
              failed++;
            }
          }

          if (created > 0 && failed === 0) {
            notify(`${created} endorsement${created > 1 ? "s" : ""} configured`, { type: "success" });
          } else if (failed > 0) {
            notify(
              `${created} endorsement${created !== 1 ? "s" : ""} created, ${failed} failed — you can add them from the policy page`,
              { type: "warning" }
            );
          }
        }
      }

      // ── Create lasered claimants ──
      if (lasered_claimants && lasered_claimants.length > 0) {
        const result = await createLaseredClaimantEntries(policyId, lasered_claimants, (resource, data) =>
          createRecord(resource, { data }, { returnPromise: true })
        );
        laseredEntryToasts(result).forEach(({ message, type }) => notify(message, { type }));
      }

      // ── Reporting cadence toast ──
      if ((formData as PolicyCreateType).reporting_cadence) {
        notify("Automated reporting task generation is now active.", { type: "success" });
      }
    },
    [createRecord, notify]
  );

  return (
    <CreateInDialog<PolicyCreateType>
      open={open}
      onClose={onClose}
      resource={ResourceName.POLICIES}
      defaultValues={{
        status: "active",
        specific_benefit: { has_medical_coverage: true, has_rx_coverage: true },
        aggregate_benefit: { has_medical_coverage: true, has_rx_coverage: true },
        ...(defaultClientId ? { client_id_ref: defaultClientId } : {}),
      }}
      transform={transformPolicyPayload}
      onSuccess={handlePostCreate}
    />
  );
};
